import React, { useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { withState } from "../../../services/state";

import {
  getMembers as apiGetMembers,
  getEvent as apiGetEvent,
  addPlayerToEvent as apiAddPlayerToEvent,
  removePlayerFromEvent as apiRemovePlayerFromEvent,
  updatePlayerEventFee as apiUpdatePlayerEventFee,
} from "../../../services/api";

import PageWrapper from "../../components/pageWrapper/PageWrapper";
import DialogModal from "../../components/dialogModal/DialogModal";
import Section from "../../components/section/Section";
import Label from "../../components/label/Label";
import Button from "../../components/button/Button";
import SingleLineTextInput from "../../components/singleLineTextInput/SingleLineTextInput";
import Conversation from "../../components/conversation/Conversation";
import ChatButton from "../../components/chatButton/ChatButton";
import PaymentDialogWrapper from "../../components/paymentDialog/PaymentDialogWrapper";

import EventPlayersTable from "../../tables/eventPlayersTable/EventPlayersTable";
import ToggleEventPlayersForm from "../../forms/toggleEventPlayersForm/ToggleEventPlayersForm";
import EditEventFeeForm from "../../forms/editEventFeeForm/EditEventFeeForm";

const Detail = styled("div")({
  marginBottom: 20,
});

const ChoosePlayerButtons = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
  "& > div": {
    margin: "0 5px",
  },
  [theme.breakpoints.down("sm")]: {
    display: "block",
    "& > div": {
      marginBottom: "10px",
    },
  },
}));

function EventPage({ classes, dispatch, context }) {
  const { eventId } = useParams();

  const [showAddPlayer, toggleShowAddPlayer] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [gettingMembers, setGettingMembers] = React.useState(true);
  const [members, setMembers] = React.useState([]);
  const [event, setEvent] = React.useState(true);
  // const [addingPlayerError, setAddPlayerError] = React.useState(true);
  const [addingPlayer, setAddingPlayer] = React.useState(true);
  // const [deletePlayerError, setDeletePlayerError] = React.useState(true);
  const [deletingPlayer, setDeletingPlayer] = React.useState(true);
  const [editEventFee, setEditEventFee] = React.useState(null);
  const [payEventFee, setPayEventFee] = React.useState(null);
  const [showEventMessages, setShowEventMessages] = React.useState(false);

  const getMembers = useCallback(async () => {
    try {
      if (!context.currentClub.clubId) {
        return false;
      }

      setGettingMembers(true);
      const data = await apiGetMembers(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        setMembers(data);
      }
      setGettingMembers(false);
    } catch (e) {
      console.log(e);
      setGettingMembers(false);
    }
  }, [context.currentClub.clubId, context.currentPlayer.id]);

  const getEvent = useCallback(async () => {
    try {
      const data = await apiGetEvent(eventId);
      if (data && !data.reason) {
        setEvent(data);
      }
    } catch (e) {
      console.log(e);
    }
  }, [eventId]);

  const addPlayerToEvent = async (member) => {
    try {
      setAddingPlayer(member.playerId);
      await apiAddPlayerToEvent(eventId, member.playerId);
      setAddingPlayer(false);
      // Dispatch custom event to notify sidebar to refresh player data
      window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
      getEvent();
    } catch (e) {
      setAddingPlayer(false);
      // setAddPlayerError(e.message);
    }
  };

  const removePlayerFromEvent = async (eventPlayer) => {
    try {
      setDeletingPlayer(eventPlayer.player.id);
      await apiRemovePlayerFromEvent(eventId, eventPlayer.player.id);
      // Dispatch custom event to notify sidebar to refresh player data
      window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
      getEvent();
      setDeletingPlayer(false);
    } catch (e) {
      setDeletingPlayer(false);
      // setDeletePlayerError(e.message);
    }
  };
  const updatePlayerEventFee = async () => {
    try {
      console.log("editEventFee", editEventFee);
      await apiUpdatePlayerEventFee(
        eventId,
        editEventFee.player.id,
        editEventFee.currentFee,
        editEventFee.adjustmentReasonId,
        editEventFee.newAdjustmentReason
      );
      setEditEventFee(null);
      // Dispatch custom event to notify sidebar to refresh player data
      window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
      getEvent();
    } catch (e) {
      // setDeletePlayerError(e.message);
    }
  };

  const isPlayerSelected = (member) => {
    return event.players.find(
      (eventPlayer) => eventPlayer.player.id === member.playerId
    );
  };

  const payForEvent = async (eventPlayer) => {
    if (eventPlayer && eventPlayer.currentFee > 0 && !eventPlayer.isPaid) {
      setPayEventFee({ ...eventPlayer, eventGuid: event.guid });
    }
  };

  const reorderClubPlayers = () => {
    setMembers([]);
    setSearchTerm("");

    setTimeout(() => {
      setMembers(
        members.sort((a, b) => {
          let aSelected = isPlayerSelected(a);
          let bSelected = isPlayerSelected(b);

          if (aSelected && !bSelected) {
            return -1;
          } else if (bSelected && !aSelected) {
            return 1;
          } else {
            let aName = `${a.firstName} ${a.lastName}`;
            let bName = `${b.firstName} ${b.lastName}`;

            if (aName !== bName) {
              return bName > aName ? -1 : 1;
            } else {
              return b.id > a.id ? -1 : 1;
            }
          }
        })
      );
    }, 10);
  };

  const renderEventTime = () => {
    if (event.eventStartTime && event.eventEndTime) {
      return `${event.eventStartTime} - ${event.eventEndTime}`;
    } else if (event.eventStartTime) {
      return event.eventStartTime;
    } else if (event.eventEndTime) {
      return event.eventEndTime;
    }
  };

  useEffect(() => {
    getMembers();
    getEvent();
  }, [getMembers, getEvent]);

  return (
    <PageWrapper
      showHeader={true}
      showSidebar={true}
      title="Event"
      showLoading={gettingMembers}
      actionButtons={
        <div>
          {(context.currentClub.isTreasurer ||
            event.isCaptainForAvailabilityRequestTeam) && (
            <Link to={`/events/${event.id}/edit`}>
              <IconButton color="default">
                <EditIcon />
              </IconButton>
            </Link>
          )}
          <ChatButton
            unreadCount={event.currentPlayerFee?.unreadMessageCount || 0}
            changeEvent={() =>
              setEvent((prev) => ({
                ...prev,
                currentPlayerFee: {
                  ...prev.currentPlayerFee,
                  unreadMessageCount: 0,
                },
              }))
            }
            onOpenConversation={() => setShowEventMessages(true)}
          />
        </div>
      }
    >
      <Section>
        {event && event.type && event.team && (
          <div>
            <Detail>
              <Label title="NAME" />
              <div>{event.name}</div>
            </Detail>
            <Detail>
              <Label title="EVENT DATE" />
              <div>
                {format(new Date(event.eventDate), "do MMMM yyyy")}{" "}
                {renderEventTime()}
              </div>
            </Detail>
            <Detail>
              <Label title="EVENT LOCATION" />
              <div>{event.eventLocation}</div>
            </Detail>
            <Detail>
              <Label title="EVENT TYPE" />
              <div>{event.type.name}</div>
            </Detail>
            <Detail>
              <Label title="TEAM" />
              <div>{event.team.name}</div>
            </Detail>
          </div>
        )}
      </Section>

      <Section>
        <EventPlayersTable
          players={event.players}
          additionalButtons={
            (context.currentClub.isTreasurer ||
              event.isCaptainForEventTeam) && [
              {
                title: "Edit players",
                onPress: () => toggleShowAddPlayer(true),
              },
            ]
          }
          onEditEventFee={
            (context.currentClub.isTreasurer || event.isCaptainForEventTeam) &&
            ((eventPlayer) => setEditEventFee(eventPlayer))
          }
          onPayEventFee={(eventPlayer) => payForEvent(eventPlayer)}
          isTreasurerOrCaptain={
            context.currentClub.isTreasurer || event.isCaptainForEventTeam
          }
          myPlayer={context.currentPlayer}
        />
      </Section>

      {showAddPlayer && (
        <DialogModal
          title="Choose event players"
          onClose={() => toggleShowAddPlayer(false)}
          cancelText="Close"
        >
          <ChoosePlayerButtons>
            <SingleLineTextInput
              placeholder="Search"
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
            />
            <div>
              <Button title="REORDER" onClick={() => reorderClubPlayers()} />
            </div>
          </ChoosePlayerButtons>
          <ToggleEventPlayersForm
            members={members.filter(
              (member) =>
                `${member.firstName} ${member.lastName}`
                  .toLowerCase()
                  .indexOf(searchTerm.toLowerCase()) !== -1
            )}
            players={event.players}
            eventPlayers={event.players || []}
            onAddPlayer={(membership) => {
              addPlayerToEvent(membership);
            }}
            addingPlayer={addingPlayer}
            onRemovePlayer={(eventPlayer) => {
              removePlayerFromEvent(eventPlayer);
            }}
            deletingPlayer={deletingPlayer}
            onClose={() => toggleShowAddPlayer(false)}
            filterPaidPlayers={true}
          />
        </DialogModal>
      )}

      {editEventFee && (
        <DialogModal
          title="Edit event fee"
          onClose={() => setEditEventFee(null)}
          submitText="Update"
          onSubmit={() => updatePlayerEventFee()}
        >
          <EditEventFeeForm
            payload={editEventFee}
            storePayload={(value) => setEditEventFee(value)}
          />
        </DialogModal>
      )}

      {payEventFee && (
        <PaymentDialogWrapper
          eventFee={payEventFee}
          onClose={() => setPayEventFee(null)}
          onSuccess={() => {
            setPayEventFee(null);
            // Dispatch custom event to notify sidebar to refresh player data
            window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
            getEvent();
          }}
          context={context}
        />
      )}

      {showEventMessages && (
        <DialogModal
          title="Messages"
          onClose={() => {
            setShowEventMessages(null);
            // Dispatch custom event to notify sidebar to refresh player data
            window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
          }}
          cancelText="Close"
        >
          <Conversation
            context={context}
            eventId={eventId}
            isCaptainForTeam={
              context.currentClub.isTreasurer || event.isCaptainForEventTeam
            }
          />
        </DialogModal>
      )}
    </PageWrapper>
  );
}

export default withState(EventPage);
